import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { toast } from "sonner";
import {
  ANDROID_STORE_URL, DISMISS_KEY, DISMISS_MS, appPathFor, buildIntentUrl, isAndroidPhoneBrowser,
  missingFallbackUrl, shouldOfferApp, stripAppMissingFlag
} from "@/lib/app-handoff";
import { readHandoffPath } from "@/lib/handoff-memory";

const readDismissedUntil = (): number => {
  try { return Number(localStorage.getItem(DISMISS_KEY) ?? 0) || 0; } catch { return 0; }
};
const isStandalone = (): boolean => {
  try { return window.matchMedia?.("(display-mode: standalone)").matches === true; } catch { return false; }
};
const inIframe = (): boolean => {
  try { return window.self !== window.top; } catch { return true; }
};

/**
 * For menus that offer "Open in app" themselves (the Reels screens). `open(path?)` hands the app `path`
 * (default: the page being shown). `available` is true only on an Android phone browser (never iPhone, never desktop).
 */
export function useOpenInApp() {
  const { pathname, search } = useLocation();
  const available = typeof navigator !== "undefined" && isAndroidPhoneBrowser(navigator.userAgent) && !isStandalone() && !inIframe();
  function open(pathOverride?: string) {
    const appPath = pathOverride ?? appPathFor(pathname, search, readHandoffPath()) ?? "/";
    const fallback = ANDROID_STORE_URL || missingFallbackUrl(window.location.origin, pathname, search);
    window.location.href = buildIntentUrl(appPath, fallback);
  }
  return { available, open };
}

/** A slim card at the bottom on Android phones: "Open in the app". Mounted once, inside <BrowserRouter>.
 *  It sits above the phone tab bar when there is one (AppLayout publishes the bar's height as --bottom-nav-h), otherwise at the screen edge. */
export default function OpenInAppBanner() {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { open } = useOpenInApp();
  const [dismissedUntil, setDismissedUntil] = useState<number>(() => readDismissedUntil());

  // Chrome sends the person back here with ?app=missing when no installed app took the intent: say so once, then clean the URL.
  useEffect(() => {
    const { search: cleaned, had } = stripAppMissingFlag(search);
    if (!had) return;
    navigate({ pathname, search: cleaned }, { replace: true });
    toast.info(t("handoff.notInstalled"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const show = shouldOfferApp({
    ua: typeof navigator === "undefined" ? "" : navigator.userAgent,
    pathname, standalone: isStandalone(), inIframe: inIframe(), dismissedUntil, now: Date.now()
  });
  if (!show) return null;

  function dismiss() {
    const until = Date.now() + DISMISS_MS;
    try { localStorage.setItem(DISMISS_KEY, String(until)); } catch { /* private mode: it just shows again next visit */ }
    setDismissedUntil(until);
  }

  return (
    <div
      role="region"
      aria-label={t("handoff.region")}
      className="fixed inset-x-3 z-30 mx-auto flex max-w-md items-center gap-3 rounded-2xl bg-popover p-3 shadow-xl"
      style={{ bottom: "calc(var(--bottom-nav-h, 0rem) + 0.75rem + env(safe-area-inset-bottom))" }}
    >
      <img src="/icon-192.png" alt="" width={44} height={44} className="h-11 w-11 shrink-0 rounded-[22%]" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-base font-bold leading-tight">{t("handoff.title")}</p>
        <p className="truncate text-sm text-muted-foreground">{t("handoff.subtitle")}</p>
      </div>
      <button type="button" onClick={() => open()} className="shrink-0 rounded-full bg-foreground px-5 py-2.5 text-sm font-bold text-background">
        {t("handoff.open")}
      </button>
      <button type="button" onClick={dismiss} aria-label={t("handoff.dismiss")} className="-mr-1 shrink-0 p-2 text-muted-foreground">
        <X className="h-5 w-5" />
      </button>
    </div>
  );
}
